import http, { request } from './http';
import type { Result } from './fileApi';

/** 与后端 CadUploadResultVO 一致（taskId 建议为十进制字符串，避免大 Long 在 JS 中丢精度） */
export interface XindiCadUploadResult {
  taskId?: string | number | null;
  inputNdsFile: string;
  mainFile: string;
  /** BOM 等项目场景：MinIO 中的源文件对象键，供下载与结构解析 */
  sourceObjectKey?: string | null;
  uploadJobId?: string | null;
  uploadSessionId?: string | null;
  status?: string | null;
}

export interface XindiCadUploadSession {
  sessionId: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  uploadedChunks: number[];
  status: string;
}

export interface XindiCadUploadJob {
  uploadJobId: string;
  uploadSessionId: string;
  status: string;
  fileName: string;
  fileSize: number;
  sourceObjectKey?: string | null;
  taskId?: string | null;
  inputNdsFile?: string | null;
  errorMessage?: string | null;
}

/** 与后端 QueryTaskData 一致 */
export interface XindiQueryTaskData {
  status?: number | null;
  errorDescription?: string | null;
  errorDetail?: string | null;
  outputFiles?: string | null;
  outFilePathType?: number | null;
  outputNdsFiles?: string | null;
  errorType?: number | null;
  previewURL?: string | null;
  jsonURL?: string | null;
  imgURL?: string | null;
}

/** 与后端 LwPreviewUrlsVO 一致 */
export interface XindiPreviewUrls {
  previewPageUrl?: string | null;
  ndsOrPackageUrl?: string | null;
  jsonUrl?: string | null;
  thumbnailUrl?: string | null;
  taskStatus?: number | null;
  engineErrorDescription?: string | null;
}

/** 与后端 XindiLwClientConfigVO 一致 */
export interface XindiClientConfig {
  enabled: boolean;
  configured?: boolean;
  explicitEnabled?: boolean;
  fingerHttp?: string | null;
  viewerSdkBaseUrl?: string | null;
  fileServiceBaseUrl?: string | null;
  maxUploadBytes?: number | null;
}

function isSuccessEnvelope(r: unknown): r is Result<unknown> {
  if (r == null || typeof r !== 'object') return false;
  const o = r as Record<string, unknown>;
  return o.success === true || o.success === 'true';
}

function unwrap<T>(r: Result<T>, fallback: string): T {
  if (!isSuccessEnvelope(r) || (r as Result<T>).data == null) {
    const msg =
      typeof (r as Result<T>).message === 'string' && (r as Result<T>).message!.trim()
        ? (r as Result<T>).message
        : fallback;
    throw new Error(msg || fallback);
  }
  return r.data as T;
}

/** 兼容后端 boolean、字符串 "true"/"false"、数字 0/1 */
export function isXindiLwEnabledFlag(raw: unknown): boolean {
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

/** 新迪引擎是否可用：与后端 isOperational 一致（enabled 或已配齐凭证） */
export function isXindiLwOperational(cfg: XindiClientConfig | null | undefined): boolean {
  if (!cfg) return false;
  return isXindiLwEnabledFlag(cfg.enabled) || isXindiLwEnabledFlag(cfg.configured);
}

function normalizeXindiClientConfig(raw: unknown): XindiClientConfig {
  const o = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const maxRaw = o.maxUploadBytes;
  let maxUploadBytes: number | null = null;
  if (typeof maxRaw === 'number' && Number.isFinite(maxRaw) && maxRaw > 0) {
    maxUploadBytes = maxRaw;
  } else if (typeof maxRaw === 'string' && maxRaw.trim()) {
    const n = Number(maxRaw);
    if (Number.isFinite(n) && n > 0) maxUploadBytes = n;
  }
  return {
    enabled: isXindiLwEnabledFlag(o.enabled),
    configured: isXindiLwEnabledFlag(o.configured),
    explicitEnabled: isXindiLwEnabledFlag(o.explicitEnabled),
    fingerHttp: typeof o.fingerHttp === 'string' ? o.fingerHttp : null,
    viewerSdkBaseUrl: typeof o.viewerSdkBaseUrl === 'string' ? o.viewerSdkBaseUrl : null,
    fileServiceBaseUrl: typeof o.fileServiceBaseUrl === 'string' ? o.fileServiceBaseUrl : null,
    maxUploadBytes,
  };
}

/** 浏览器端 Viewer 配置（无密钥） */
export async function fetchXindiClientConfig(): Promise<XindiClientConfig> {
  const res = await request<unknown>(http.get('/lw-engine/client-config'));
  if (res != null && typeof res === 'object') {
    const o = res as Record<string, unknown>;
    if (isSuccessEnvelope(res) && o.data != null) {
      return normalizeXindiClientConfig(o.data);
    }
    if ('enabled' in o || 'fingerHttp' in o || 'maxUploadBytes' in o) {
      return normalizeXindiClientConfig(res);
    }
  }
  throw new Error('获取新迪配置失败：响应格式异常');
}

export async function uploadXindiCad(
  file: File,
  options?: { taskDescription?: string; priority?: string; mainFile?: string },
): Promise<XindiCadUploadResult> {
  return uploadXindiCadWithProgress(file, options);
}

export async function uploadXindiCadWithProgress(
  file: File,
  options?: {
    taskDescription?: string;
    priority?: string;
    mainFile?: string;
    /** true：写入 MinIO 源文件（BOM 项目图纸可下载）；false：仅提交引擎（3D Viewer） */
    persistSource?: boolean;
    projectId?: number;
    uploadSessionId?: string;
    onUploadSession?: (sessionId: string) => void;
    onProgress?: (percent: number) => void;
  },
): Promise<XindiCadUploadResult> {
  if (options?.persistSource && options.projectId != null && options.projectId > 0) {
    return uploadXindiCadBomWithProgress(file, {
      projectId: options.projectId,
      uploadSessionId: options.uploadSessionId,
      onUploadSession: options.onUploadSession,
      onProgress: options.onProgress,
    });
  }
  return uploadXindiCadStreamWithProgress(file, options);
}

async function uploadXindiCadBomWithProgress(
  file: File,
  options: {
    projectId: number;
    uploadSessionId?: string;
    onUploadSession?: (sessionId: string) => void;
    onProgress?: (percent: number) => void;
  },
): Promise<XindiCadUploadResult> {
  const projectId = options.projectId;
  const session = options.uploadSessionId
    ? await getXindiCadUploadSession(projectId, options.uploadSessionId)
    : await createXindiCadUploadSession(projectId, file);
  if (session.fileSize !== file.size || session.fileName !== file.name) {
    throw new Error('续传文件与原上传会话不匹配，请重新上传');
  }
  options.onUploadSession?.(session.sessionId);

  const uploaded = new Set(session.uploadedChunks || []);
  let completedBytes = [...uploaded].reduce((sum, index) => {
    const start = index * session.chunkSize;
    return sum + Math.max(0, Math.min(session.chunkSize, file.size - start));
  }, 0);
  const report = (loaded = 0, base = completedBytes) => {
    options.onProgress?.(Math.min(100, Math.round(((base + loaded) / file.size) * 100)));
  };

  for (let index = 0; index < session.totalChunks; index += 1) {
    if (uploaded.has(index)) continue;
    const start = index * session.chunkSize;
    const end = Math.min(file.size, start + session.chunkSize);
    const chunk = file.slice(start, end);
    let lastError: unknown;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        await putXindiCadUploadChunk(projectId, session.sessionId, index, chunk, (loaded) => {
          report(loaded, completedBytes);
        });
        lastError = undefined;
        break;
      } catch (error) {
        lastError = error;
      }
    }
    if (lastError) throw lastError;
    completedBytes += end - start;
    report();
  }

  const job = await completeXindiCadUploadSession(projectId, session.sessionId);
  return {
    taskId: job.taskId,
    inputNdsFile: job.inputNdsFile || '',
    mainFile: job.fileName || file.name,
    sourceObjectKey: job.sourceObjectKey,
    uploadJobId: job.uploadJobId,
    uploadSessionId: job.uploadSessionId || session.sessionId,
    status: job.status,
  };
}

async function createXindiCadUploadSession(projectId: number, file: File): Promise<XindiCadUploadSession> {
  const res = await request<Result<XindiCadUploadSession>>(
    http.post(`/projects/${projectId}/bom/cad-upload/sessions`, {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || 'application/octet-stream',
    }),
  );
  return unwrap(res as unknown as Result<XindiCadUploadSession>, '创建上传会话失败');
}

async function getXindiCadUploadSession(projectId: number, sessionId: string): Promise<XindiCadUploadSession> {
  const res = await request<Result<XindiCadUploadSession>>(
    http.get(`/projects/${projectId}/bom/cad-upload/sessions/${encodeURIComponent(sessionId)}`),
  );
  return unwrap(res as unknown as Result<XindiCadUploadSession>, '查询上传会话失败');
}

async function putXindiCadUploadChunk(
  projectId: number,
  sessionId: string,
  index: number,
  chunk: Blob,
  onProgress: (loaded: number) => void,
): Promise<XindiCadUploadSession> {
  const res = await request<Result<XindiCadUploadSession>>(
    http.put(
      `/projects/${projectId}/bom/cad-upload/sessions/${encodeURIComponent(sessionId)}/chunks/${index}`,
      chunk,
      {
        timeout: 180_000,
        headers: { 'Content-Type': 'application/octet-stream' },
        onUploadProgress: (event: { loaded: number }) => onProgress(event.loaded),
      },
    ),
  );
  return unwrap(res as unknown as Result<XindiCadUploadSession>, '上传分片失败');
}

async function completeXindiCadUploadSession(projectId: number, sessionId: string): Promise<XindiCadUploadJob> {
  const res = await request<Result<XindiCadUploadJob>>(
    http.post(`/projects/${projectId}/bom/cad-upload/sessions/${encodeURIComponent(sessionId)}/complete`),
  );
  return unwrap(res as unknown as Result<XindiCadUploadJob>, '完成上传失败');
}

export async function queryXindiCadUploadJob(projectId: number, jobId: string): Promise<XindiCadUploadJob> {
  const res = await request<Result<XindiCadUploadJob>>(
    http.get(`/projects/${projectId}/bom/cad-upload/jobs/${encodeURIComponent(jobId)}`, { timeout: 120_000 }),
  );
  return unwrap(res as unknown as Result<XindiCadUploadJob>, '查询 CAD 上传任务失败');
}

export async function retryXindiCadUploadJob(projectId: number, jobId: string): Promise<XindiCadUploadJob> {
  const res = await request<Result<XindiCadUploadJob>>(
    http.post(`/projects/${projectId}/bom/cad-upload/jobs/${encodeURIComponent(jobId)}/retry`),
  );
  return unwrap(res as unknown as Result<XindiCadUploadJob>, '重试 CAD 上传任务失败');
}

/** 大文件流式上传（不经 multipart） */
async function uploadXindiCadStreamWithProgress(
  file: File,
  options?: {
    taskDescription?: string;
    priority?: string;
    mainFile?: string;
    persistSource?: boolean;
    projectId?: number;
    onProgress?: (percent: number) => void;
  },
): Promise<XindiCadUploadResult> {
  const params = new URLSearchParams();
  if (options?.taskDescription) params.set('taskDescription', options.taskDescription);
  if (options?.priority) params.set('priority', options.priority);
  if (options?.mainFile) params.set('mainFile', options.mainFile);
  if (options?.persistSource) params.set('persistSource', 'true');
  if (options?.projectId != null && options.projectId > 0) {
    params.set('projectId', String(options.projectId));
  }
  const qs = params.toString();
  const url = qs ? `/lw-engine/cad/upload-stream?${qs}` : '/lw-engine/cad/upload-stream';
  const mainFile = options?.mainFile?.trim() || file.name || 'model';

  const res = await request<Result<XindiCadUploadResult>>(
    http.post(url, file, {
      timeout: 2_700_000,
      headers: {
        'Content-Type': 'application/octet-stream',
        'X-File-Name': encodeURIComponent(mainFile),
      },
      onUploadProgress: (ev: { loaded: number; total?: number }) => {
        const total = ev.total ?? file.size;
        if (total > 0 && options?.onProgress) {
          options.onProgress(Math.min(100, Math.round((ev.loaded / total) * 100)));
        }
      },
    }),
  );
  const r = res as unknown as Result<XindiCadUploadResult>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '上传失败');
  }
  return r.data;
}

export async function queryXindiTask(taskId: string | number): Promise<XindiQueryTaskData> {
  const res = await request<Result<XindiQueryTaskData>>(
    http.get(`/lw-engine/tasks/${encodeURIComponent(String(taskId))}`, { timeout: 120_000 }),
  );
  const r = res as unknown as Result<XindiQueryTaskData>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '查询任务失败');
  }
  return r.data;
}

/** 轻量化结构树解析诊断（BOM 失败排查） */
export interface XindiStructureTreeDiagnostic {
  taskId: number;
  diagnosticComplete: boolean;
  parseSuccess: boolean;
  taskStatus?: number | null;
  engineErrorDescription?: string | null;
  engineErrorDetail?: string | null;
  jsonUrlResolved?: string | null;
  jsonUrlSource?: string | null;
  jsonFetchOk?: boolean | null;
  httpStatus?: number | null;
  jsonBodyLength?: number | null;
  jsonRootType?: string | null;
  topLevelKeys?: string[];
  nodesFromWalk?: number | null;
  nodesFromPartArrays?: number | null;
  nodesFromFlatten?: number | null;
  parsedNodeCount?: number | null;
  fallbackNodeUsed?: boolean | null;
  modelTreeChildrenCount?: number | null;
  singlePartFromXindi?: boolean | null;
  alternateJsonUrlTried?: string | null;
  sampleNodes?: Array<{ node_id?: string; part_name?: string; drawing_no?: string }>;
  failureReason?: string | null;
  bodyPreview?: string | null;
  hint?: string | null;
  diagnostic_api?: string | null;
}

export async function fetchXindiStructureTreeDiagnostic(
  taskId: string | number,
  drawingName?: string,
): Promise<XindiStructureTreeDiagnostic> {
  const tid = encodeURIComponent(String(taskId));
  const body = drawingName?.trim() ? { drawingName: drawingName.trim() } : {};
  const res = await request<Result<XindiStructureTreeDiagnostic>>(
    http.post(`/lw-engine/tasks/${tid}/structure-tree/diagnostic`, body, { timeout: 120_000 }),
  );
  const r = res as unknown as Result<XindiStructureTreeDiagnostic>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '图纸结构诊断失败');
  }
  return r.data;
}

export async function fetchXindiPreviewUrls(taskId: string | number): Promise<XindiPreviewUrls> {
  const res = await request<Result<XindiPreviewUrls>>(
    http.get(`/lw-engine/tasks/${encodeURIComponent(String(taskId))}/preview-urls`, { timeout: 120_000 }),
  );
  const r = res as unknown as Result<XindiPreviewUrls>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '获取预览地址失败');
  }
  return r.data;
}

/** 引擎轻量化资源镜像到 MinIO 后的返回 */
export interface XindiLwMirrorPersistResult {
  objectKey: string;
  accessUrl: string;
}

/** 服务端从引擎 URL 拉取并写入 MinIO，返回脱敏 accessUrl 供 Viewer 作 src */
export async function mirrorXindiLightweightToMinio(
  sourceUrl: string,
  clientJobId: string,
): Promise<XindiLwMirrorPersistResult> {
  const res = await request<Result<XindiLwMirrorPersistResult>>(
    http.post('/lw-engine/lightweight/mirror', { sourceUrl, clientJobId }, { timeout: 900_000 }),
  );
  const r = res as unknown as Result<XindiLwMirrorPersistResult>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '镜像轻量化数据失败');
  }
  return r.data;
}

export async function deleteXindiLightweightFromMinio(objectKey: string): Promise<void> {
  const res = await request<Result<unknown>>(
    http.delete('/lw-engine/lightweight', { params: { objectKey }, timeout: 120_000 }),
  );
  const r = res as unknown as Result<unknown>;
  if (!isSuccessEnvelope(r)) {
    throw new Error((r as Result<unknown>).message || '删除轻量化对象失败');
  }
}

/** 与后端 LwOfflineHtmlBuildResultVO 一致 */
export interface XindiLwOfflineHtmlBuildResult {
  htmlTaskId?: string | number | null;
  objectPrefix: string;
  viewPath: string;
}

/** CAD 完成后：引擎 zip → InsertHtmlConvertTask → 解压到 MinIO，返回同源 iframe 路径 */
export async function buildXindiOfflineHtml(body: {
  clientJobId: string;
  sourceEngineZipUrl: string;
  viewerType?: number;
  htmlType?: number;
}): Promise<XindiLwOfflineHtmlBuildResult> {
  const res = await request<Result<XindiLwOfflineHtmlBuildResult>>(
    http.post('/lw-engine/offline-html/build', body, { timeout: 900_000 }),
  );
  const r = res as unknown as Result<XindiLwOfflineHtmlBuildResult>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '构建离线 HTML 失败');
  }
  return r.data;
}

export async function deleteXindiOfflineHtmlPrefix(objectPrefix: string): Promise<void> {
  const res = await request<Result<unknown>>(
    http.delete('/lw-engine/offline-html', { params: { objectPrefix }, timeout: 120_000 }),
  );
  const r = res as unknown as Result<unknown>;
  if (!isSuccessEnvelope(r)) {
    throw new Error((r as Result<unknown>).message || '删除离线 HTML 失败');
  }
}

export interface XindiOfflineHtmlShareCreated {
  publicId: string;
  recipientUserId: number;
}

export async function createXindiOfflineHtmlShares(body: {
  recipientUserIds: number[];
  objectPrefix: string;
  indexEntryPath: string;
  displayName?: string;
}): Promise<XindiOfflineHtmlShareCreated[]> {
  const res = await request<Result<XindiOfflineHtmlShareCreated[]>>(
    http.post('/lw-engine/offline-html/shares', body, { timeout: 120_000 }),
  );
  const r = res as unknown as Result<XindiOfflineHtmlShareCreated[]>;
  if (!isSuccessEnvelope(r) || !Array.isArray(r.data)) {
    throw new Error(r.message || '分享失败');
  }
  return r.data;
}

export interface XindiSharePreviewToken {
  viewPath: string;
  displayName?: string | null;
  archived: boolean;
}

export async function getXindiSharePreviewToken(publicId: string): Promise<XindiSharePreviewToken> {
  const res = await request<Result<XindiSharePreviewToken>>(
    http.get(`/lw-engine/offline-html/shares/${encodeURIComponent(publicId)}/preview-token`, { timeout: 60_000 }),
  );
  const r = res as unknown as Result<XindiSharePreviewToken>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '获取预览失败');
  }
  return r.data;
}

export interface XindiShareArchiveResult {
  objectPrefix: string;
  viewPath: string;
  clientJobId: string;
}

export async function copyXindiShareToArchive(publicId: string): Promise<XindiShareArchiveResult> {
  const res = await request<Result<XindiShareArchiveResult>>(
    http.post(`/lw-engine/offline-html/shares/${encodeURIComponent(publicId)}/copy-to-archive`, {}, { timeout: 600_000 }),
  );
  const r = res as unknown as Result<XindiShareArchiveResult>;
  if (!isSuccessEnvelope(r) || r.data == null) {
    throw new Error(r.message || '复制到归档失败');
  }
  return r.data;
}
